<template>
  <!-- Modal fullscreen siguiendo el patrón del proyecto -->
  <el-dialog
    :visible="visible"
    fullscreen
    :show-close="false"
    :append-to-body="true"
    custom-class="sri-import-dialog"
  >
    <!-- ════ HEADER ════ -->
    <div slot="title" class="sri-modal-header">
      <div class="sri-modal-header__left">
        <img
          src="/img/sri-logo.png"
          class="sri-modal-header__logo"
          alt="SRI"
          onerror="this.style.display='none'"
        />
        <div>
          <h5 class="sri-modal-header__title">Importar facturas del SRI</h5>
          <span class="sri-modal-header__sub"
            >Comprobantes electrónicos recibidos</span
          >
        </div>
      </div>
      <button class="sri-modal-header__close" @click="handleClose">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <!-- ════ CONTENIDO ════ -->
    <div class="sri-modal-body">
      <!-- ─── CLOSE BUTTON ─── -->
      <button class="sri-modal-close-btn" @click="handleClose" title="Cerrar">
        <i class="el-icon-close"></i>
      </button>

      <!-- ─── STEP INDICATOR ─── -->
      <div class="sri-steps">
        <div class="sri-step" :class="{ active: step === 1, done: step > 1 }">
          <span class="sri-step__num">{{ step > 1 ? "✓" : "1" }}</span>
          <span class="sri-step__label">Credenciales</span>
        </div>
        <div class="sri-step-line" :class="{ done: step > 1 }"></div>
        <div class="sri-step" :class="{ active: step === 2, done: step > 2 }">
          <span class="sri-step__num">{{ step > 2 ? "✓" : "2" }}</span>
          <span class="sri-step__label">Seleccionar</span>
        </div>
        <div class="sri-step-line" :class="{ done: step > 2 }"></div>
        <div class="sri-step" :class="{ active: step === 3 }">
          <span class="sri-step__num">3</span>
          <span class="sri-step__label">Resultado</span>
        </div>
      </div>

      <!-- ════════ STEP 1: CREDENCIALES ════════ -->
      <div v-if="step === 1" class="sri-card">
        <div class="sri-card__header">
          <i class="el-icon-lock sri-card__icon"></i>
          <h6 class="sri-card__title">
            Ingresa tus credenciales del portal SRI
          </h6>
          <p class="sri-card__desc">
            Usa el mismo usuario y contraseña de
            <a
              href="https://srienlinea.sri.gob.ec"
              target="_blank"
              rel="noopener"
              >srienlinea.sri.gob.ec</a
            >. Tus credenciales no se almacenan.
          </p>
        </div>

        <div class="sri-form">
          <div class="sri-form-row">
            <div class="sri-form-group">
              <label class="sri-label">Usuario (RUC / Cédula)</label>
              <input
                v-model="form.usuario"
                type="text"
                class="sri-input"
                :class="{ 'sri-input--error': errors.usuario }"
                placeholder="Ej: 1712345678001"
                autocomplete="username"
              />
              <span v-if="errors.usuario" class="sri-error">{{
                errors.usuario
              }}</span>
            </div>
            <div class="sri-form-group">
              <label class="sri-label">Contraseña</label>
              <div class="sri-input-wrap">
                <input
                  v-model="form.clave"
                  :type="showPass ? 'text' : 'password'"
                  class="sri-input"
                  :class="{ 'sri-input--error': errors.clave }"
                  placeholder="Contraseña del portal SRI"
                  autocomplete="current-password"
                  @keyup.enter="consultarSRI"
                />
                <button
                  class="sri-eye-btn"
                  type="button"
                  @click="showPass = !showPass"
                >
                  <i :class="showPass ? 'el-icon-view' : 'el-icon-minus'"></i>
                </button>
              </div>
              <span v-if="errors.clave" class="sri-error">{{
                errors.clave
              }}</span>
            </div>
          </div>

          <div class="sri-form-row">
            <div class="sri-form-group">
              <label class="sri-label">Fecha desde</label>
              <input
                v-model="form.date_from"
                type="date"
                class="sri-input"
                :max="form.date_to || today"
              />
            </div>
            <div class="sri-form-group">
              <label class="sri-label">Fecha hasta</label>
              <input
                v-model="form.date_to"
                type="date"
                class="sri-input"
                :min="form.date_from"
                :max="today"
              />
            </div>
          </div>

          <div v-if="authError" class="sri-alert sri-alert--error">
            <i class="el-icon-warning-outline"></i>
            {{ authError }}
          </div>

          <!-- Opción guardar credenciales -->
          <label class="sri-save-creds">
            <input type="checkbox" v-model="saveCredentials" />
            <span>
              Guardar credenciales para <strong>sync automático semanal</strong>
              <small>(cifradas, puedes eliminarlas desde Gastos)</small>
            </span>
          </label>

          <div class="sri-form-footer">
            <button class="sri-btn sri-btn--secondary" @click="handleClose">
              Cancelar
            </button>
            <button
              class="sri-btn sri-btn--primary"
              :disabled="loadingPreview"
              @click="consultarSRI"
            >
              <i v-if="loadingPreview" class="el-icon-loading"></i>
              <span>{{
                loadingPreview
                  ? pollStatus || "Consultando SRI..."
                  : "Consultar facturas"
              }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ════════ STEP 2: PREVIEW / SELECCIÓN ════════ -->
      <div v-if="step === 2" class="sri-card sri-card--wide">
        <div class="sri-card__header">
          <i
            class="el-icon-document-checked sri-card__icon sri-card__icon--green"
          ></i>
          <h6 class="sri-card__title">
            {{ previewItems.length }} factura(s) encontradas
          </h6>
          <p class="sri-card__desc">
            Selecciona las que quieres importar. Las marcadas en gris ya fueron
            importadas anteriormente.
          </p>
        </div>

        <!-- Tabla de selección -->
        <div class="sri-preview-table-wrap">
          <table class="sri-preview-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    @change="toggleAll"
                  />
                </th>
                <th>Proveedor (Emisor)</th>
                <th>N° Comprobante</th>
                <th>Fecha</th>
                <th class="text-right">IVA</th>
                <th class="text-right">Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in previewItems"
                :key="item.claveAcceso"
                :class="{ 'row--imported': item.already_imported }"
              >
                <td class="col-check">
                  <input
                    type="checkbox"
                    :value="item.claveAcceso"
                    v-model="selectedClaves"
                    :disabled="item.already_imported"
                  />
                </td>
                <td class="col-vendor">{{ item.razonSocialEmisor || "—" }}</td>
                <td class="col-num">{{ item.numeroComprobante || "—" }}</td>
                <td class="col-date">{{ formatDate(item.fechaEmision) }}</td>
                <td class="text-right col-iva">
                  ${{ formatAmount(item.iva) }}
                </td>
                <td class="text-right col-amount">
                  ${{ formatAmount(item.importeTotal) }}
                </td>
                <td class="col-status">
                  <span
                    v-if="item.already_imported"
                    class="sri-badge sri-badge--grey"
                    >Ya importada</span
                  >
                  <span v-else class="sri-badge sri-badge--green">Nueva</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" class="text-right tfoot-label">
                  Totales seleccionados:
                </td>
                <td class="text-right">
                  ${{ formatAmount(selectedIvaTotal) }}
                </td>
                <td class="text-right">${{ formatAmount(selectedTotal) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="sri-form-footer">
          <button class="sri-btn sri-btn--secondary" @click="step = 1">
            ← Volver
          </button>
          <span class="sri-selection-count"
            >{{ selectedClaves.length }} seleccionada(s)</span
          >
          <button
            class="sri-btn sri-btn--primary"
            :disabled="selectedClaves.length === 0 || loadingImport"
            @click="importarSeleccionadas"
          >
            <i v-if="loadingImport" class="el-icon-loading"></i>
            <span>{{
              loadingImport
                ? "Importando..."
                : `Importar ${selectedClaves.length} factura(s)`
            }}</span>
          </button>
        </div>
      </div>

      <!-- ════════ STEP 3: RESULTADO ════════ -->
      <div v-if="step === 3" class="sri-card sri-card--result">
        <div class="sri-result">
          <div class="sri-result__icon">
            <i class="el-icon-circle-check"></i>
          </div>
          <h5 class="sri-result__title">Importación completada</h5>

          <div class="sri-result__stats">
            <div class="sri-result__stat sri-result__stat--green">
              <span class="sri-result__stat-num">{{
                importResult.imported.length
              }}</span>
              <span class="sri-result__stat-label">Importadas</span>
            </div>
            <div class="sri-result__stat sri-result__stat--grey">
              <span class="sri-result__stat-num">{{
                importResult.skipped.length
              }}</span>
              <span class="sri-result__stat-label">Ya existían</span>
            </div>
            <div
              class="sri-result__stat"
              :class="
                importResult.errors.length > 0
                  ? 'sri-result__stat--red'
                  : 'sri-result__stat--grey'
              "
            >
              <span class="sri-result__stat-num">{{
                importResult.errors.length
              }}</span>
              <span class="sri-result__stat-label">Errores</span>
            </div>
          </div>

          <!-- Detalle de errores si los hay -->
          <div v-if="importResult.errors.length > 0" class="sri-result__errors">
            <p class="sri-result__errors-title">Errores:</p>
            <ul>
              <li v-for="(err, i) in importResult.errors" :key="i">
                {{ err.sri_numero_comprobante || err.sri_clave_acceso }}:
                {{ err.reason }}
              </li>
            </ul>
          </div>

          <div class="sri-form-footer sri-form-footer--center">
            <button class="sri-btn sri-btn--secondary" @click="handleClose">
              Cerrar
            </button>
            <button class="sri-btn sri-btn--primary" @click="verGastos">
              Ver en Gastos <i class="el-icon-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import Api from "@/config/Api";

const defaultForm = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return {
    usuario: "",
    clave: "",
    date_from: `${year}-${month}-01`,
    date_to: `${year}-${month}-${day}`
  };
};

export default {
  name: "SriImportModal",

  props: {
    visible: { type: Boolean, default: false }
  },

  data() {
    return {
      step: 1,
      form: defaultForm(),
      errors: {},
      showPass: false,
      authError: "",
      loadingPreview: false,
      loadingImport: false,
      previewItems: [],
      selectedClaves: [],
      importResult: { imported: [], skipped: [], errors: [] },
      today: new Date().toISOString().split("T")[0],
      saveCredentials: false,
      // Polling
      jobToken: null,
      pollInterval: null,
      pollStatus: ""
    };
  },

  computed: {
    businessId() {
      return this.$store.getters.getUser?.business_id;
    },
    allSelected() {
      const importable = this.previewItems.filter(i => !i.already_imported);
      return (
        importable.length > 0 &&
        this.selectedClaves.length === importable.length
      );
    },
    selectedItems() {
      return this.previewItems.filter(i =>
        this.selectedClaves.includes(i.claveAcceso)
      );
    },
    selectedTotal() {
      return this.selectedItems.reduce(
        (sum, i) => sum + (i.importeTotal || 0),
        0
      );
    },
    selectedIvaTotal() {
      return this.selectedItems.reduce((sum, i) => sum + (i.iva || 0), 0);
    }
  },

  methods: {
    handleClose() {
      this.$emit("close");
      // Reset con pequeño delay para no ver el parpadeo
      setTimeout(() => this.reset(), 300);
    },

    reset() {
      this.step = 1;
      this.form = defaultForm();
      this.errors = {};
      this.authError = "";
      this.previewItems = [];
      this.selectedClaves = [];
      this.importResult = { imported: [], skipped: [], errors: [] };
      this.showPass = false;
      this.saveCredentials = false;
      this.stopPolling();
      this.jobToken = null;
      this.pollStatus = "";
    },

    validate() {
      this.errors = {};
      if (!this.form.usuario.trim())
        this.errors.usuario = "Ingresa tu usuario del portal SRI";
      if (!this.form.clave.trim())
        this.errors.clave = "Ingresa tu contraseña del portal SRI";
      return Object.keys(this.errors).length === 0;
    },

    async consultarSRI() {
      if (!this.validate()) return;
      this.authError = "";
      this.loadingPreview = true;
      this.pollStatus = "Iniciando consulta al SRI...";

      try {
        // Paso 1: encolar job — responde en < 1s
        const res = await Api().post("/sri/purchases/preview", {
          ...this.form,
          business_id: this.businessId
        });

        if (!res?.data?.success) {
          this.authError = res?.data?.message || "Error al iniciar la consulta";
          this.loadingPreview = false;
          return;
        }

        this.jobToken = res.data.token;

        // Guardar credenciales si el usuario lo solicitó (no esperar)
        if (this.saveCredentials) {
          this.persistCredentials();
        }

        // Paso 2: polling hasta que el job termine
        this.startPolling();
      } catch (e) {
        this.authError =
          e?.response?.data?.message || "Error de conexión con el SRI";
        this.loadingPreview = false;
      }
    },

    startPolling() {
      this.pollStatus = "Conectando con el portal SRI...";
      this.pollInterval = setInterval(async () => {
        try {
          const res = await Api().get(`/sri/purchases/job/${this.jobToken}`, {
            params: { business_id: this.businessId }
          });
          const job = res?.data;

          if (!job) return;

          if (job.status === "queued") {
            this.pollStatus = "En cola, iniciando scraper...";
          } else if (job.status === "running") {
            this.pollStatus = "Extrayendo comprobantes del portal SRI...";
          } else if (job.status === "done") {
            this.stopPolling();
            this.previewItems = job.data || [];
            this.selectedClaves = this.previewItems
              .filter(i => !i.already_imported)
              .map(i => i.claveAcceso);
            this.loadingPreview = false;
            this.step = 2;
          } else if (job.status === "failed") {
            this.stopPolling();
            this.authError = job.message || "Error al consultar el SRI";
            this.loadingPreview = false;
          }
        } catch (e) {
          // Error de red — seguir intentando (no detener polling)
        }
      }, 3000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    toggleAll(e) {
      if (e.target.checked) {
        this.selectedClaves = this.previewItems
          .filter(i => !i.already_imported)
          .map(i => i.claveAcceso);
      } else {
        this.selectedClaves = [];
      }
    },

    async importarSeleccionadas() {
      if (this.selectedClaves.length === 0) return;
      this.loadingImport = true;

      try {
        const res = await Api().post("/sri/purchases/import", {
          token: this.jobToken,
          clave_accesos: this.selectedClaves,
          business_id: this.businessId
        });

        if (res?.data?.success || res?.data?.imported) {
          this.importResult = {
            imported: res.data.imported || [],
            skipped: res.data.skipped || [],
            errors: res.data.errors || []
          };
          this.step = 3;
          this.$emit("imported", this.importResult.imported.length);
        } else {
          this.$notify({
            type: "error",
            title: "Error",
            message: res?.data?.message || "Error al importar"
          });
        }
      } catch (e) {
        this.$notify({
          type: "error",
          title: "Error",
          message: e?.response?.data?.message || "Error de conexión"
        });
      } finally {
        this.loadingImport = false;
      }
    },

    verGastos() {
      this.handleClose();
      this.$emit("go-to-expenses");
    },

    formatAmount(val) {
      if (!val && val !== 0) return "0.00";
      return parseFloat(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    },

    async persistCredentials() {
      try {
        await Api().post("/sri/credentials", {
          usuario: this.form.usuario,
          clave: this.form.clave,
          business_id: this.businessId
        });
        this.$emit("credentials-saved");
      } catch (e) {
        // Silencioso — no interrumpir el flujo de importación
      }
    }
  }
};
</script>

<style lang="scss">
// Estilos del dialog (no scoped — necesita penetrar el el-dialog)
.sri-import-dialog {
  .el-dialog__header {
    padding: 0;
  }
  .el-dialog__body {
    padding: 0;
    overflow: hidden;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
}
</style>

<style lang="scss" scoped>
$teal: #3ecfb4;
$dark: #2d2b55;
$green: #1d9e75;
$grey: #909399;
$border: #e4eaf6;
$red: #e24b4a;

// ── Close button flotante ─────────────────────────────────
.sri-modal-close-btn {
  position: fixed;
  top: 16px;
  right: 20px;
  z-index: 9999;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid $border;
  color: $grey;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.15s;
  &:hover {
    color: $dark;
    border-color: $dark;
    background: #f0f2f7;
  }
}

// ── Header ───────────────────────────────────────────────
.sri-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__logo {
    height: 28px;
    object-fit: contain;
  }
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: $dark;
    margin: 0;
  }
  &__sub {
    font-size: 12px;
    color: $grey;
  }
  &__close {
    background: none;
    border: none;
    cursor: pointer;
    color: $grey;
    font-size: 20px;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.15s;
    &:hover {
      color: $dark;
      background: #f0f2f7;
    }
  }
}

// ── Body ─────────────────────────────────────────────────
.sri-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f7f8fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

// ── Steps ────────────────────────────────────────────────
.sri-steps {
  display: flex;
  align-items: center;
  gap: 0;
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 14px 24px;
  width: 100%;
  max-width: 560px;
}

.sri-step {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;

  &__num {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #f0f2f7;
    color: $grey;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
  }
  &__label {
    font-size: 12px;
    font-weight: 500;
    color: $grey;
    @media (max-width: 576px) {
      display: none;
    }
  }

  &.active {
    .sri-step__num {
      background: $dark;
      color: #fff;
    }
    .sri-step__label {
      color: $dark;
    }
  }
  &.done {
    .sri-step__num {
      background: $green;
      color: #fff;
    }
    .sri-step__label {
      color: $green;
    }
  }
}

.sri-step-line {
  width: 40px;
  height: 2px;
  background: #e4eaf6;
  flex-shrink: 0;
  &.done {
    background: $green;
  }
}

// ── Card ─────────────────────────────────────────────────
.sri-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 600px;

  &--wide {
    max-width: 100%;
  }
  &--result {
    max-width: 480px;
  }

  &__header {
    text-align: center;
    margin-bottom: 24px;
  }
  &__icon {
    font-size: 32px;
    color: $dark;
    display: block;
    margin-bottom: 8px;
    &--green {
      color: $green;
    }
  }
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: $dark;
    margin: 0 0 6px;
  }
  &__desc {
    font-size: 13px;
    color: $grey;
    margin: 0;
    a {
      color: $teal;
    }
  }
}

// ── Formulario ───────────────────────────────────────────
.sri-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.sri-form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.sri-label {
  font-size: 12px;
  font-weight: 600;
  color: $dark;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sri-input-wrap {
  position: relative;
}

.sri-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid $border;
  border-radius: 10px;
  font-size: 13px;
  color: $dark;
  background: #fafbfd;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus {
    border-color: $teal;
  }
  &--error {
    border-color: $red;
  }
}

.sri-eye-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: $grey;
  cursor: pointer;
  font-size: 15px;
  &:hover {
    color: $dark;
  }
}

.sri-error {
  font-size: 11px;
  color: $red;
}

.sri-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 14px;
  &--error {
    background: #fff0f0;
    color: $red;
    border: 1px solid #f7c1c1;
  }
  i {
    font-size: 16px;
  }
}

.sri-form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  &--center {
    justify-content: center;
  }
}

.sri-selection-count {
  flex: 1;
  font-size: 13px;
  color: $grey;
}

// ── Botones ──────────────────────────────────────────────
.sri-btn {
  padding: 9px 22px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--primary {
    background: $teal;
    color: #fff;
    &:hover:not(:disabled) {
      opacity: 0.85;
    }
  }
  &--secondary {
    background: #fff;
    color: $dark;
    border: 1px solid $border;
    &:hover {
      border-color: $dark;
    }
  }
}

// ── Preview table ─────────────────────────────────────────
.sri-preview-table-wrap {
  overflow-x: auto;
  margin-bottom: 0;
}

.sri-preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead tr {
    background: #f7f8fa;
  }
  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    color: $grey;
    padding: 8px 12px;
    text-align: left;
    border-bottom: 2px solid $border;
    white-space: nowrap;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f0f2f7;
    color: #4b5563;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr:hover:not(.row--imported) td {
    background: #f9fafb;
  }
  tfoot td {
    padding: 10px 12px;
    border-top: 2px solid $border;
    font-weight: 600;
    color: $dark;
  }

  .row--imported td {
    opacity: 0.5;
  }
  .col-check {
    width: 36px;
    text-align: center;
  }
  .col-vendor {
    font-weight: 500;
    color: $dark;
  }
  .col-num {
    font-family: monospace;
    font-size: 12px;
    white-space: nowrap;
  }
  .col-date {
    white-space: nowrap;
    color: $grey;
  }
  .col-iva {
    white-space: nowrap;
  }
  .col-amount {
    font-weight: 600;
    color: $dark;
    white-space: nowrap;
  }
  .text-right {
    text-align: right;
  }
  .tfoot-label {
    text-align: right;
    color: $grey;
    font-size: 12px;
  }
}

// ── Badges ────────────────────────────────────────────────
.sri-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 6px;
  &--green {
    background: #e8f8f4;
    color: $green;
  }
  &--grey {
    background: #f1efe8;
    color: $grey;
  }
}

// ── Resultado ────────────────────────────────────────────
.sri-result {
  text-align: center;
  &__icon {
    font-size: 48px;
    color: $green;
    margin-bottom: 12px;
  }
  &__title {
    font-size: 18px;
    font-weight: 600;
    color: $dark;
    margin: 0 0 20px;
  }
  &__stats {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-bottom: 20px;
  }
  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 12px 20px;
    border-radius: 10px;
    background: #f7f8fa;
    min-width: 80px;
    &--green {
      background: #e8f8f4;
    }
    &--grey {
      background: #f1efe8;
    }
    &--red {
      background: #fff0f0;
    }
  }
  &__stat-num {
    font-size: 28px;
    font-weight: 700;
    color: $dark;
  }
  &__stat-label {
    font-size: 12px;
    color: $grey;
  }
  &--green .sri-result__stat-num {
    color: $green;
  }
  &--red .sri-result__stat-num {
    color: $red;
  }

  &__errors {
    text-align: left;
    background: #fff0f0;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
    &-title {
      font-size: 12px;
      font-weight: 600;
      color: $red;
      margin: 0 0 6px;
    }
    ul {
      margin: 0;
      padding-left: 16px;
      font-size: 12px;
      color: $red;
    }
  }
}

// ── Guardar credenciales ──────────────────────────────────
.sri-save-creds {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #f7f8fa;
  border: 1px solid $border;
  border-radius: 8px;
  margin-bottom: 4px;
  cursor: pointer;

  input[type="checkbox"] {
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: $teal;
    cursor: pointer;
  }

  span {
    font-size: 12px;
    color: #4b5563;
    line-height: 1.5;

    strong {
      color: $dark;
    }

    small {
      display: block;
      font-size: 11px;
      color: $grey;
      margin-top: 1px;
    }
  }
}
</style>
